// Admin › OTA Releases › Register updates: each app's coming version, and the
// changes each pull request merged to main makes to the help register, in
// merge order (a whole-release draft groups by screen instead). Opening one
// goes to RegisterReviewPage, which takes the whole window.
// docs/core/operations/REGISTER_UPDATES.md

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  isDone,
  needsCheck,
  nzDay,
  otherPrs,
  plainTitle,
  useRegisterRelease,
  useRegisterReleases,
  type RegisterChange,
  type RegisterMerge,
  type RegisterRelease,
} from './data/register';
import { byMerge, byScreen, changeHint, inOrder, itemDone, itemNeedsCheck, itemsOf, Kbd, KindTag, ProgressSegments, releaseState, StatusGlyph, type Item } from './register/parts';
import { AppIcon } from './changelog/AppIcon';

const SHEET_URL = 'https://docs.google.com/spreadsheets/d/1cghSko6N33gHrNs8tyBAHMPo9ZpMpE_MWnfEwC_Adi0/edit';

type Filter = 'all' | 'open' | 'conflict' | 'done';
const FILTERS: { key: Filter; label: string; test: (c: RegisterChange) => boolean }[] = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'open', label: 'To check', test: (c) => c.status === 'open' },
  { key: 'conflict', label: 'Look again', test: (c) => c.status === 'conflict' },
  { key: 'done', label: 'Done', test: isDone },
];

const left = (r: RegisterRelease) => (r.counts ? r.counts.open + r.counts.conflict : 0);

export function RegisterUpdatesPage() {
  const [params, setParams] = useSearchParams();
  const releases = useRegisterReleases();
  const list = useMemo(() => releases.data ?? [], [releases.data]);
  const chosen = list.find((r) => r.id === params.get('release')) ?? list.find((r) => left(r) > 0) ?? list[0];
  const detail = useRegisterRelease(chosen?.id);
  const [filter, setFilter] = useState<Filter>('all');
  const navigate = useNavigate();

  const merges = useMemo(() => detail.data?.merges ?? [], [detail.data]);
  const changes = useMemo(() => inOrder(detail.data?.changes ?? [], merges), [detail.data, merges]);
  const mergeOf = (c: RegisterChange) => merges.find((m) => m.id === c.merge_id);
  const items = useMemo(() => itemsOf(changes), [changes]);
  const nextItem = items.find((i) => i.changes.some((c) => c.status === 'conflict')) ?? items.find(itemNeedsCheck);
  const next = nextItem?.changes.find(needsCheck) ?? nextItem?.changes[0];
  const nextMerge = next && mergeOf(next);
  const reviewUrl = (c: RegisterChange) => `/admin/releases/register/${chosen!.id}?change=${c.id}`;
  const itemUrl = (i: Item) => (i.kind === 'bundle' ? `/admin/releases/register/${chosen!.id}?bundle=${encodeURIComponent(i.key)}` : reviewUrl(i.change));

  useEffect(() => {
    if (!next || !chosen) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== 'Enter' || e.metaKey || e.ctrlKey || e.altKey || (t && t.closest('input, textarea, select, button, a, [contenteditable]'))) return;
      navigate(itemUrl(nextItem!));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const shown = FILTERS.find((f) => f.key === filter)!;
  const groups = byScreen(changes.filter(shown.test));
  const mergeGroups = byMerge(changes.filter(shown.test), merges).filter((g) => g.changes.length > 0);
  const quiet = merges.filter((m) => !changes.some((c) => c.merge_id === m.id));
  const total = changes.length;
  const retire = changes.filter((c) => c.kind === 'retire').length;
  const added = changes.filter((c) => c.kind === 'new').length;

  return (
    <div className="max-w-[1120px] space-y-7 px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-[26px] font-bold leading-none tracking-tight text-white">Register updates</h1>
          <p className="text-[15px] text-slate-400">Each change merged to main, ready to check on a Scout before it's released.</p>
        </div>
        <a
          href={SHEET_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-2 text-sm font-medium text-slate-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          Open the register <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>

      {releases.isLoading && <p className="text-[15px] text-slate-400">Loading…</p>}
      {releases.isError && <p className="text-[15px] text-red-400">Couldn't load the releases.</p>}
      {releases.isSuccess && list.length === 0 && (
        <div className="rounded-2xl border border-slate-400/15 bg-slate-800/45 px-6 py-10 text-center">
          <p className="text-base font-semibold text-white">Nothing to check yet</p>
          <p className="mt-1.5 text-[15px] text-slate-400">When a change is merged to main, what it changes in the register shows up here.</p>
        </div>
      )}

      {list.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.slice(0, 6).map((r) => {
            const on = r.id === chosen?.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setParams({ release: r.id })}
                aria-pressed={on}
                className={`flex flex-col gap-4 rounded-2xl border p-5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                  on ? 'border-cyan-400/35 bg-cyan-400/[0.06]' : 'border-slate-400/15 bg-slate-800/45 hover:bg-slate-800/70'
                }`}
              >
                <span className="flex items-center gap-3.5">
                  <AppIcon app={r.app} size={44} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[17px] font-semibold text-white">{r.module} {r.version}</span>
                    <span className={`block text-[13px] ${r.released ? 'text-slate-400' : 'text-cyan-300'}`}>{releaseState(r)}</span>
                  </span>
                  <span className="text-sm text-slate-400">
                    {left(r) > 0 ? <><span className="text-xl font-bold text-white">{left(r)}</span> to check</> : 'All checked'}
                  </span>
                </span>
                {on && changes.length > 0 ? <ProgressSegments changes={changes} /> : <Bar release={r} />}
              </button>
            );
          })}
        </div>
      )}

      {chosen && detail.isSuccess && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
              <h2 className="text-[19px] font-semibold text-white">{chosen.module} {chosen.version}</h2>
              <span className="text-sm text-slate-400">
                {chosen.released ? '' : 'Not released yet · '}
                {merges.length > 0 && `${merges.length} merge${merges.length === 1 ? '' : 's'} · `}
                {total} row{total === 1 ? '' : 's'}{added ? ` · ${added} new` : ''}{retire ? ` · ${retire} to retire` : ''}
              </span>
            </div>
            <div role="tablist" aria-label="Show" className="flex gap-0.5 rounded-[10px] bg-slate-400/10 p-[3px]">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={`min-h-[32px] rounded-[7px] px-3 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                    filter === f.key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {f.label} <span className={filter === f.key ? 'text-slate-400' : 'text-slate-500'}>{changes.filter(f.test).length}</span>
                </button>
              ))}
            </div>
          </div>

          {next && (
            <Link
              to={itemUrl(nextItem!)}
              className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-cyan-600 px-5 py-4 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <span className="text-[13px] font-bold uppercase tracking-wider text-cyan-100">Next up</span>
              <span className="flex min-w-0 flex-1 basis-full flex-col gap-0.5 sm:basis-0">
                <span className="text-lg font-bold">{nextItem?.kind === 'bundle' ? nextItem.title : next.element}</span>
                <span className="text-sm text-cyan-50">
                  {nextMerge
                    ? `${nextMerge.pr_number ? `From #${nextMerge.pr_number}, ` : 'From '}${plainTitle(nextMerge.title)} · ${nextItem?.kind === 'bundle' ? `${nextItem.changes.length} rows' paths, checked together` : `${next.screen} · ${changeHint(next).toLowerCase()}`}`
                    : `${next.screen} · ${changeHint(next).toLowerCase()} · check on a Scout running ${chosen.version}`}
                </span>
              </span>
              <span className="inline-flex min-h-[44px] items-center gap-2.5 rounded-[10px] bg-white px-4 text-[15px] font-semibold text-cyan-700">
                Start checking <span className="hidden sm:inline-flex"><Kbd>Enter</Kbd></span>
              </span>
            </Link>
          )}
          {!next && total > 0 && (
            <p className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.06] px-5 py-4 text-[15px] text-emerald-200">
              Everything in {chosen.module} {chosen.version} is checked. Accepted changes go into the register within the hour.
            </p>
          )}

          {merges.length > 0 && (
            <div className="gap-4 lg:columns-2">
              {mergeGroups.map((g) => (
                <MergeCard key={g.merge?.id ?? 'release'} merge={g.merge} changes={g.changes} href={itemUrl} />
              ))}
              {filter === 'all' && quiet.length > 0 && (
                <section className="mb-4 flex break-inside-avoid flex-col gap-1.5 rounded-2xl border border-dashed border-slate-400/25 px-4 py-3.5">
                  <span className="text-sm font-semibold text-slate-300">
                    {quiet.length} merge{quiet.length === 1 ? '' : 's'} changed nothing in the register
                  </span>
                  <span className="text-[13px] leading-relaxed text-slate-400">
                    {quiet.map((m) => `${m.pr_number ? `#${m.pr_number} ` : ''}${plainTitle(m.title)}`).join(' · ')}
                  </span>
                </section>
              )}
            </div>
          )}

          {merges.length === 0 && <div className="gap-4 sm:columns-2 lg:columns-3">
            {groups.map((g) => (
              <section key={g.screen} className="mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45">
                <header className="flex items-center justify-between px-4 pb-2.5 pt-3.5">
                  <h3 className="text-[13px] font-semibold text-slate-200">{g.screen}</h3>
                  <span className="text-[13px] text-slate-500">{g.changes.filter(isDone).length} of {g.changes.length}</span>
                </header>
                {g.changes.map((c) => (
                  <Link
                    key={c.id}
                    to={reviewUrl(c)}
                    className="flex min-h-[52px] items-center gap-3 border-t border-slate-400/10 px-4 py-1.5 hover:bg-slate-400/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500"
                  >
                    <StatusGlyph status={c.status} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={`truncate text-sm font-medium ${isDone(c) ? 'text-slate-400' : 'text-slate-100'}`}>{c.element}</span>
                      <span className="text-[13px] text-slate-500">{changeHint(c)}</span>
                    </span>
                    <KindTag kind={c.kind} />
                  </Link>
                ))}
              </section>
            ))}
          </div>}
          {(merges.length ? mergeGroups : groups).length === 0 && <p className="text-[15px] text-slate-400">Nothing here.</p>}
        </>
      )}
      {chosen && detail.isError && <p className="text-[15px] text-red-400">Couldn't load {chosen.module} {chosen.version}.</p>}
    </div>
  );
}

/** One merged pull request and the rows it changes. */
function MergeCard({ merge, changes, href }: { merge: RegisterMerge | null; changes: RegisterChange[]; href: (i: Item) => string }) {
  const done = changes.filter(isDone).length;
  const all = done === changes.length;
  return (
    <section className={`mb-4 break-inside-avoid overflow-hidden rounded-2xl border bg-slate-800/45 ${all ? 'border-slate-400/10' : 'border-slate-400/15'}`}>
      <header className="flex items-start gap-3 px-4 pb-2.5 pt-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="text-[15px] font-semibold text-slate-100">{merge ? plainTitle(merge.title) : 'Drafted with the whole release'}</h3>
          {merge && (
            <span className="text-[13px] text-slate-400">
              {merge.pr_number && merge.url
                ? <a href={merge.url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-white">#{merge.pr_number}</a>
                : merge.pr_number ? `#${merge.pr_number}` : merge.shas[0]?.slice(0, 8)}
              {' · '}{merge.author.split(' ')[0]}{merge.merged_at ? ` · merged ${nzDay(merge.merged_at)}` : ''}
            </span>
          )}
        </div>
        <span className={`flex-none text-[13px] ${all ? 'text-emerald-400' : 'text-slate-500'}`}>{all ? 'All checked' : `${done} of ${changes.length} checked`}</span>
      </header>
      {itemsOf(changes).map((item) => {
        if (item.kind === 'bundle') return <BundleRow key={item.id} item={item} href={href(item)} />;
        const c = item.change;
        const also = otherPrs(c, merge ?? undefined);
        return (
          <Link
            key={c.id}
            to={href(item)}
            className="flex min-h-[52px] items-center gap-3 border-t border-slate-400/10 px-4 py-1.5 hover:bg-slate-400/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500"
          >
            <StatusGlyph status={c.status} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className={`truncate text-sm font-medium ${isDone(c) ? 'text-slate-400' : 'text-slate-100'}`}>{c.element}</span>
              <span className="text-[13px] text-slate-500">
                {c.screen} · {changeHint(c).toLowerCase()}{also.length ? ` · also ${also.map((n) => `#${n}`).join(', ')}` : ''}
              </span>
            </span>
            <KindTag kind={c.kind} />
          </Link>
        );
      })}
    </section>
  );
}

/** Several rows whose paths changed the same way, as one entry. */
function BundleRow({ item, href }: { item: Extract<Item, { kind: 'bundle' }>; href: string }) {
  const done = itemDone(item);
  return (
    <Link
      to={href}
      className="flex min-h-[52px] items-center gap-3 border-t border-slate-400/10 px-4 py-1.5 hover:bg-slate-400/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500"
    >
      <span aria-hidden="true" className={`inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] text-[13px] font-bold ${done ? 'bg-emerald-400/15 text-emerald-400' : 'shadow-[inset_0_0_0_1.5px_#475569]'}`}>{done ? '✓' : ''}</span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate text-sm font-medium ${done ? 'text-slate-400' : 'text-slate-100'}`}>{item.title}</span>
        <span className="text-[13px] text-slate-500">{item.changes.length} rows' paths, checked together</span>
      </span>
    </Link>
  );
}

/** A release's progress from its counts alone, before its changes are loaded. */
function Bar({ release }: { release: RegisterRelease }) {
  const c = release.counts;
  const total = release.total ?? 0;
  if (!c || total === 0) return <div className="h-1.5 rounded-sm bg-slate-400/25" aria-hidden="true" />;
  const pct = (n: number) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-1.5 gap-[3px] overflow-hidden" aria-hidden="true">
      <span className="rounded-sm bg-emerald-400" style={{ width: pct(c.applied + c.accepted + c.rejected) }} />
      <span className="rounded-sm bg-amber-400" style={{ width: pct(c.conflict) }} />
      <span className="flex-1 rounded-sm bg-slate-400/25" />
    </div>
  );
}
